import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, SparklesIcon } from 'lucide-react';
import { trackEvent } from '../utils/tracking';

/* ------------------------------------------------------------------ */
/* QUIZ DATA                                                           */
/* ------------------------------------------------------------------ */

type Phase = 'H' | 'E' | 'R';

/** The four possible result outcomes: a dominant phase, or the neutral GENERAL path. */
type ResultKind = Phase | 'GENERAL';
type PhaseScore = Record<Phase, number>;

/** Declared objection/motivation collected from neutral (non-phase) answers. */
type MotivationTag = 'objecion_constancia' | 'primera_vez';
type Answer = {
  id: string;
  label: string;
  /** The phase this answer signals, or null when the answer is neutral (no phase signal). */
  phase: Phase | null;
  /** Weight applied to `phase` when resolving the result. 0 for neutral answers. */
  weight: number;
  /** Present only on neutral answers that declare a specific motivation/objection (Q2). */
  motivationTag?: MotivationTag;
};
type Question = {
  id: string;
  prompt: string;
  /** Optional small muted line shown above the question prompt (used on Q3). */
  connectorLabel?: string;
  answers: Answer[];
};

/**
 * QUESTION CONTENT + PHASE SIGNAL
 * Q1 and Q2 are the only questions that carry phase signal — Q3 is a format
 * preference and NEVER affects the result phase (weight 0 on every option).
 *   H = Hormonal     Q1 weight 7 (primary signal), Q2 weight 3 (secondary signal)
 *   E = Enzimática   same weighting
 *   R = Restaurativa same weighting
 */
const QUESTIONS: Question[] = [{
  id: 'q1',
  prompt: '¿Qué cambio notaste últimamente?',
  answers: [{
    id: 'q1a1',
    label: 'Siento que mi cuerpo cambió y me cuesta volver a sentirme como antes',
    phase: 'H',
    weight: 7
  }, {
    id: 'q1a2',
    label: 'Mi digestión y la forma en que me siento después de comer cambiaron',
    phase: 'E',
    weight: 7
  }, {
    id: 'q1a3',
    label: 'Siento que necesito recuperar equilibrio y una rutina que pueda sostener',
    phase: 'R',
    weight: 7
  }, {
    id: 'q1a4',
    label: 'Varias de estas',
    phase: null,
    weight: 0
  }]
}, {
  id: 'q2',
  prompt: '¿Qué fue lo que te hizo buscar una solución ahora?',
  answers: [{
    id: 'q2a1',
    label: 'Siento que mi cuerpo cambió y no sé cómo acompañarlo',
    phase: 'H',
    weight: 3
  }, {
    id: 'q2a2',
    label: 'Quiero sentirme más liviana y con mejor digestión',
    phase: 'E',
    weight: 3
  }, {
    id: 'q2a3',
    label: 'Quiero recuperar equilibrio y poder sostener una rutina',
    phase: 'R',
    weight: 3
  }, {
    id: 'q2a4',
    label: 'Probé otras cosas y no logré sostenerlas',
    phase: null,
    weight: 0,
    motivationTag: 'objecion_constancia'
  }, {
    id: 'q2a5',
    label: 'Simplemente quiero empezar a cuidarme de otra manera',
    phase: null,
    weight: 0,
    motivationTag: 'primera_vez'
  }]
}, {
  id: 'q3',
  prompt: '¿Preferís algo...?',
  connectorLabel: 'Para mostrarte el protocolo de la forma que más te sirva',
  answers: [
  // Q3 never carries phase signal — it only ever informs which product aspect
  // (format/structure) gets emphasized in the result, not which phase is shown.
  {
    id: 'q3a1',
    label: 'Simple y fácil de seguir',
    phase: null,
    weight: 0
  }, {
    id: 'q3a2',
    label: 'Con recetas y rutinas',
    phase: null,
    weight: 0
  }, {
    id: 'q3a3',
    label: 'Un plan completo de 21 días',
    phase: null,
    weight: 0
  }]
}];

/**
 * PHASE ICONS
 * Simple lettered-circle marks matching the H·E·R phase language used
 * throughout the rest of the funnel (ProtocolSection's "H / E / R" ghost letters).
 */
function PhaseIconH({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true" data-id="element-295" data-mp="22sb15d2f2ekc">
      <circle cx="12" cy="12" r="10.5" stroke="currentColor" strokeWidth="1.2" data-id="element-296" data-mp="27qmalmosun21" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontFamily="serif" fill="currentColor" data-id="element-297" data-mp="20rctshuzy99">
        H
      </text>
    </svg>;
}
function PhaseIconE({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true" data-id="element-298" data-mp="b17csrze8n9d">
      <circle cx="12" cy="12" r="10.5" stroke="currentColor" strokeWidth="1.2" data-id="element-299" data-mp="703zac4tj3bb" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontFamily="serif" fill="currentColor" data-id="element-300" data-mp="3fggmtcn9hirm">
        E
      </text>
    </svg>;
}
function PhaseIconR({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true" data-id="element-301" data-mp="3o8ik5221aixo">
      <circle cx="12" cy="12" r="10.5" stroke="currentColor" strokeWidth="1.2" data-id="element-302" data-mp="3oalg02pvb6ab" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontFamily="serif" fill="currentColor" data-id="element-303" data-mp="3jfxrw4ys0dw9">
        R
      </text>
    </svg>;
}

/**
 * GENERAL ICON
 * Neutral mark representing "all three phases together" (three dots,
 * no single letter) — visually distinct from PhaseIconH/E/R.
 */
function PhaseIconGeneral({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true" data-id="element-304" data-mp="2meft078k2nhz">
      <circle cx="12" cy="12" r="10.5" stroke="currentColor" strokeWidth="1.2" data-id="element-305" data-mp="27pjd5o09c66t" />
      <circle cx="8.5" cy="12" r="1.2" fill="currentColor" data-id="element-306" data-mp="2cnl2gqoa11qq" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" data-id="element-307" data-mp="2clqj3gsnlhor" />
      <circle cx="15.5" cy="12" r="1.2" fill="currentColor" data-id="element-308" data-mp="30egthlkwrswx" />
    </svg>;
}

/** Display metadata for every possible result outcome, including the neutral GENERAL path. */
const RESULT_META: Record<ResultKind, {
  name: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}> = {
  H: {
    name: 'Hormonal',
    icon: PhaseIconH
  },
  E: {
    name: 'Enzimática',
    icon: PhaseIconE
  },
  R: {
    name: 'Restaurativa',
    icon: PhaseIconR
  },
  GENERAL: {
    name: 'H.E.R. completo',
    icon: PhaseIconGeneral
  }
};

/**
 * SCORING — deterministic decision tree, no API calls, no additive Q3 influence.
 * Only Q1 (weight 7, primary signal) and Q2 (weight 3, secondary signal) carry
 * phase signal; Q3 never participates. Audit trail:
 *
 *   1. Q1 has a phase AND Q2 has a phase, and they MATCH   → that phase wins.
 *   2. Q1 has a phase AND Q2 has a phase, but they DIFFER  → compare weighted
 *      scores (Q1=7 vs Q2=3); Q1's phase wins since it's the primary signal.
 *   3. Q1 has a phase AND Q2 is neutral (motivation tag)   → Q1's phase wins.
 *   4. Q1 is neutral ("Varias de estas") AND Q2 has a phase → Q2's phase
 *      breaks the tie and wins.
 *   5. Q1 is neutral AND Q2 is neutral                     → GENERAL (no
 *      single phase dominates — an explicit fourth result, never a fallback
 *      guess at H/E/R).
 */
function resolveQuizResult(selected: Answer[]): {
  result: ResultKind;
  scores: PhaseScore;
  motivationTags: MotivationTag[];
} {
  const q1 = selected[0];
  const q2 = selected[1];
  const scores: PhaseScore = {
    H: 0,
    E: 0,
    R: 0
  };
  if (q1?.phase) scores[q1.phase] += q1.weight;
  if (q2?.phase) scores[q2.phase] += q2.weight;
  const motivationTags = [q1?.motivationTag, q2?.motivationTag].filter((tag): tag is MotivationTag => Boolean(tag));
  let result: ResultKind;
  if (q1?.phase && q2?.phase) {
    if (q1.phase === q2.phase) {
      // Case 1: both questions point to the same phase.
      result = q1.phase;
    } else {
      // Case 2: they disagree — resolve via weighted scores (Q1's 7 beats Q2's 3).
      const order: Phase[] = ['H', 'E', 'R'];
      result = order.reduce<Phase>((best, current) => scores[current] > scores[best] ? current : best, order[0]);
    }
  } else if (q1?.phase && !q2?.phase) {
    // Case 3: Q1 has a phase, Q2 is a neutral motivation-tag answer.
    result = q1.phase;
  } else if (!q1?.phase && q2?.phase) {
    // Case 4: Q1 was "Varias de estas" (neutral), Q2 breaks the tie.
    result = q2.phase;
  } else {
    // Case 5: both neutral — explicit GENERAL outcome, not a guess.
    result = 'GENERAL';
  }
  return {
    result,
    scores,
    motivationTags
  };
}

/* ------------------------------------------------------------------ */
/* COMPONENT                                                           */
/* ------------------------------------------------------------------ */

type Step = 'intro' | 0 | 1 | 2 | 'result';
export function QuizSection() {
  const [step, setStep] = useState<Step>('intro');
  const [selected, setSelected] = useState<Answer[]>([]);
  const handleStart = () => {
    trackEvent('quiz_start', {
      quiz: 'her_fit'
    });
    setSelected([]);
    setStep(0);
  };
  const handleAnswer = (questionIndex: number, answer: Answer) => {
    trackEvent('quiz_answer', {
      question_number: questionIndex + 1,
      question: QUESTIONS[questionIndex].prompt,
      answer: answer.label,
      motivation_tag: answer.motivationTag ?? null
    });
    const next = [...selected.slice(0, questionIndex), answer];
    setSelected(next);
    if (questionIndex < QUESTIONS.length - 1) {
      setStep(questionIndex + 1 as Step);
      return;
    }
    const {
      result,
      scores,
      motivationTags
    } = resolveQuizResult(next);
    trackEvent('quiz_completed', {
      answers: next.map(a => a.label)
    });
    trackEvent('quiz_result', {
      phase: result,
      phase_name: RESULT_META[result].name,
      // Content-relevance framing shown to the user, kept in the payload for QA/reporting.
      // Sourced from the same constants ResultCard renders so they can never drift apart.
      phase_sentence: result === 'GENERAL' ? GENERAL_PERSONALIZATION_SENTENCE : RESULT_PERSONALIZATION_SENTENCE,
      scores,
      motivation_tags: motivationTags
    });
    setStep('result');
  };
  const handleBack = (questionIndex: number) => {
    setStep(questionIndex - 1 as Step);
  };
  const handleRetake = () => {
    trackEvent('quiz_start', {
      quiz: 'her_fit',
      retake: true
    });
    setSelected([]);
    setStep(0);
  };
  const handleResultCta = (result: ResultKind) => {
    trackEvent('cta_click_post_quiz', {
      phase: result,
      phase_name: RESULT_META[result].name,
      entry_point: 'her_fit_result'
    });
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section aria-labelledby="her-quiz-heading" className="bg-gradient-hero px-5 py-16 md:py-20 sm:px-6 font-body text-foreground" data-id="element-309" data-mp="3rnicrl05p7z9">
      <div className="mx-auto w-full max-w-[480px]" data-id="element-310" data-mp="j5ci7436u6af">
        {step === 'intro' && <IntroCard onStart={handleStart} data-id="element-311" data-mp="o5icyt4r88wq" />}

        {typeof step === 'number' && <QuestionCard key={`q-${step}`} index={step} total={QUESTIONS.length} question={QUESTIONS[step]} selectedId={selected[step]?.id} onAnswer={handleAnswer} onBack={handleBack} data-id="element-312" data-mp="97zg13hxape7" />}

        {step === 'result' && <ResultCard key="result" result={resolveQuizResult(selected).result} onCta={handleResultCta} onRetake={handleRetake} data-id="element-313" data-mp="2tygowmwks0v2" />}
      </div>
    </section>;
}

/* ------------------------------------------------------------------ */
/* INTRO                                                               */
/* ------------------------------------------------------------------ */

function IntroCard({
  onStart
}: {
  onStart: () => void;
}) {
  return <div className="her-step-in card-premium px-6 py-10 sm:px-9 sm:py-12" data-id="element-314" data-mp="1lzt3bxf2z2hm">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-gold-light/80" data-id="element-315" data-mp="1m0kkw4buezow">
        Descubrí qué fase de H.E.R. conecta más con vos
      </p>
      <div className="mt-5 h-px w-10 bg-gold/60" aria-hidden="true" data-id="element-316" data-mp="tbwyu6j9s6aa" />
      <h2 id="her-quiz-heading" className="mt-7 font-display font-medium text-[2rem] leading-[1.15] text-foreground sm:text-[2.4rem]" data-id="element-317" data-mp="172il857xmycx">
        ¿Querés saber si H.E.R. es para vos?
      </h2>
      <p className="mt-5 text-[15px] leading-relaxed text-foreground/60" data-id="element-318" data-mp="em7tdz459g47">
        Respondé 3 preguntas rápidas y te mostramos cómo puede encajar el protocolo con lo que estás
        buscando.
      </p>
      <button type="button" onClick={onStart} className="group mt-9 flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-sm border border-gold/70 px-6 text-[13px] font-medium uppercase tracking-[0.16em] text-gold-light transition-colors duration-200 ease-out hover:border-gold hover:bg-gold/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background" data-id="element-319" data-mp="29pdvzvnmn0c0">
        Empezar
        <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-320" data-mp="1qs2yoglk2gz7" />
      </button>
    </div>;
}

/* ------------------------------------------------------------------ */
/* QUESTIONS                                                           */
/* ------------------------------------------------------------------ */

type QuestionCardProps = {
  index: number;
  total: number;
  question: Question;
  selectedId?: string;
  onAnswer: (index: number, answer: Answer) => void;
  onBack: (index: number) => void;
};
function QuestionCard({
  index,
  total,
  question,
  selectedId,
  onAnswer,
  onBack
}: QuestionCardProps) {
  const progress = (index + 1) / total * 100;
  return <div className="her-step-in card-premium px-6 py-9 sm:px-9 sm:py-10" data-id="element-321" data-mp="10ivieakzmv83">
      <div className="flex items-center justify-between" data-id="element-322" data-mp="vo84f5nm1j7r">
        <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-foreground/60" data-id="element-323" data-mp="qr7xytmumeg4">
          {index + 1} de {total}
        </span>
        {index > 0 && <button type="button" onClick={() => onBack(index)} className="-mr-1 flex items-center gap-1.5 rounded-sm px-1 py-1 text-[12px] tracking-wide text-foreground/60 transition-colors duration-150 ease-out hover:text-gold-light focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-324" data-mp="lpik7688rmoc">
            <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-325" data-mp="7ty9gy2o2as0" />
            Volver
          </button>}
      </div>

      <div className="mt-3 h-px w-full bg-gold/12" role="progressbar" aria-valuemin={1} aria-valuemax={total} aria-valuenow={index + 1} aria-label={`Pregunta ${index + 1} de ${total}`} data-id="element-326" data-mp="272lnlx7f5ahg">
        <div className="h-px bg-gold transition-[width] duration-300 ease-out" style={{
        width: `${progress}%`
      }} data-id="element-327" data-mp="zt8jhdiuf60d" />
      </div>

      {question.connectorLabel && <p className="mt-8 text-[11px] font-medium uppercase tracking-[0.16em] text-foreground/60" data-id="element-328" data-mp="pt7w9ghus5qw">
          {question.connectorLabel}
        </p>}

      <h2 id="her-quiz-heading" className={`font-display font-medium text-[1.6rem] leading-[1.25] text-foreground sm:text-[1.8rem] ${question.connectorLabel ? 'mt-2' : 'mt-8'}`} data-id="element-329" data-mp="uv6z8p9ol0nb">
        {question.prompt}
      </h2>

      <div className="mt-7 flex flex-col gap-2.5" data-id="element-330" data-mp="741k6cbkece2">
        {question.answers.map(answer => {
        const isSelected = answer.id === selectedId;
        return <button key={answer.id} type="button" onClick={() => onAnswer(index, answer)} aria-pressed={isSelected} className={`flex min-h-[52px] w-full items-center justify-between gap-3 rounded-sm border px-4 py-3 text-left text-[15px] leading-snug transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-1 focus-visible:ring-gold ${isSelected ? 'border-gold/70 bg-gold/10 text-foreground' : 'border-gold/12 bg-white/[0.02] text-foreground/90 hover:border-gold/50 hover:text-foreground'}`} data-id="element-331" data-mp="c34d4ye9ss5o">
              <span data-id="element-332" data-mp="3okdv6b3mgxbg">{answer.label}</span>
              {isSelected && <CheckIcon className="h-4 w-4 shrink-0 text-gold-light" data-id="element-333" data-mp="3jmcaxmugmrye" />}
            </button>;
      })}
      </div>
    </div>;
}

/* ------------------------------------------------------------------ */
/* RESULT                                                              */
/* ------------------------------------------------------------------ */

type ResultCardProps = {
  result: ResultKind;
  onCta: (result: ResultKind) => void;
  onRetake: () => void;
};

/** Day ranges for each phase within the 21-day protocol — used by the arc lists below. */
const PHASE_DAYS: Record<Phase, string> = {
  H: 'Día 1–7',
  E: 'Día 8–14',
  R: 'Día 15–21'
};

/** Fixed protocol sequence, always shown in this order regardless of which phase is dominant. */
const PHASE_SEQUENCE: Phase[] = ['H', 'E', 'R'];

/* Short, concrete fragments for the dominant phase's highlighted bullet. */
const PHASE_HIGHLIGHTS: string[] = ['Recetas y prácticas específicas', 'Rutinas de acompañamiento'];

/**
 * Single source of truth for the single-phase (H/E/R) personalization sentence.
 * Rendered verbatim in ResultCard AND passed into the quiz_result trackEvent
 * payload, so the UI copy and the analytics payload can never drift apart.
 */
const RESULT_PERSONALIZATION_SENTENCE = 'Por lo que nos contaste, esta es una de las áreas que más conecta con lo que hoy estás buscando.';

/** Same idea, for the neutral GENERAL outcome (no single dominant phase). */
const GENERAL_PERSONALIZATION_SENTENCE = 'Por lo que nos contaste, no hay una sola fase que destaque por encima de las demás. Por eso, el recorrido completo de 21 días puede ser el mejor punto de partida.';
function ResultCard({
  result,
  onCta,
  onRetake
}: ResultCardProps) {
  const {
    name,
    icon: PhaseIcon
  } = RESULT_META[result];
  const isGeneral = result === 'GENERAL';
  const otherPhases = isGeneral ? [] : PHASE_SEQUENCE.filter(p => p !== result);
  return <div className="her-step-in card-premium !border-gold/25 px-6 py-8 sm:px-9 sm:py-10" data-id="element-334" data-mp="35gftxwpvgj6e">
      {/* TOP OF RESULT — icon + "Tu punto de partida: {name}" is the first thing seen,
          no scrolling needed to get the takeaway. Content-relevance framing, not a diagnosis. */}
      <div className="flex items-center gap-3" data-id="element-335" data-mp="3t4z5qm84re38">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold-light" data-id="element-336" data-mp="3t35usuqhi7pf">
          <PhaseIcon className="h-6 w-6" data-id="element-337" data-mp="3j7b6bizkxieq" />
        </span>
        <h2 id="her-quiz-heading" className="font-display font-medium text-[1.55rem] leading-[1.15] text-foreground sm:text-[1.8rem]" data-id="element-338" data-mp="3j6t6kcdpkm22">
          Tu punto de partida: {name}
        </h2>
      </div>

      {/* PERSONALIZATION — kept to 2-3 lines max, dynamic-ready per result. */}
      <p className="mt-4 max-w-[38ch] text-[14px] leading-relaxed text-foreground/60 sm:text-[15px]" data-id="element-339" data-mp="2xf9yqheol1ei">
        {isGeneral ? GENERAL_PERSONALIZATION_SENTENCE : RESULT_PERSONALIZATION_SENTENCE}
      </p>

      {isGeneral ?
    // GENERAL — no phase dominates, so all three get equal visual weight: same
    // border/background treatment, same text size, no gold emphasis anywhere.
    <div className="mt-6 flex flex-col gap-2" data-id="element-340" data-mp="3g69gaxjuj8xr">
          {PHASE_SEQUENCE.map(p => <div key={p} className="flex items-center justify-between rounded-sm border border-gold/12 bg-white/[0.02] px-4 py-3" data-id="element-341" data-mp="3l5ckkfs05b4o">
              <span className="text-[13.5px] text-foreground/90" data-id="element-342" data-mp="37d68p5vvtqb1">
                {PHASE_DAYS[p]} · {RESULT_META[p].name}
              </span>
            </div>)}
        </div> : <>
          {/* Compact 21-day arc — highlighted dominant phase + short bullets,
              then a de-emphasized one-liner per remaining phase. No accordion, no long copy. */}
          <div className="mt-6 rounded-sm border border-gold/40 bg-gold/[0.06] px-4 py-4" data-id="element-343" data-mp="1sh4uybznwop4">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gold-light" data-id="element-344" data-mp="1ses8bliylq5q">
              {PHASE_DAYS[result]} · Fase {name}
            </p>
            <ul className="mt-2.5 space-y-1.5" data-id="element-345" data-mp="226eozxe2ghfv">
              {PHASE_HIGHLIGHTS.map(item => <li key={item} className="flex items-start gap-2 text-[13.5px] leading-snug text-foreground/90" data-id="element-346" data-mp="1x8wdbz8nzo46">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" aria-hidden="true" data-id="element-347" data-mp="19j23lqjn6fo5" />
                  <span data-id="element-348" data-mp="14kh9m8uzgs85">{item}</span>
                </li>)}
            </ul>
          </div>

          <div className="mt-3 flex flex-col gap-1" data-id="element-349" data-mp="1jhhx2065610t">
            {otherPhases.map(p => <p key={p} className="text-[12.5px] text-foreground/60" data-id="element-350" data-mp="1egu3p7ufx83f">
                → Luego, {RESULT_META[p].name} ({PHASE_DAYS[p]})
              </p>)}
          </div>
        </>}

      {/* CTA sits close to the list above — no long scroll between takeaway and action. */}
      <button type="button" onClick={() => onCta(result)} className="mt-5 flex min-h-[56px] w-full items-center justify-center gap-2.5 rounded-sm bg-gold px-6 text-[14px] font-semibold uppercase tracking-[0.14em] text-[#0a0807] transition-colors duration-200 ease-out hover:bg-gold-light focus:outline-none focus-visible:ring-1 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background" data-id="element-351" data-mp="2pyvakhx0irjx">
        <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-352" data-mp="bdyllsggsf3t" />
        Quiero empezar H.E.R.
      </button>

      <button type="button" onClick={onRetake} className="mx-auto mt-4 block rounded-sm px-2 py-1 text-[12px] tracking-wide text-foreground/60 underline decoration-gold/20 underline-offset-4 transition-colors duration-150 ease-out hover:text-gold-light focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-353" data-mp="6fnbdgm9c2l2">
        Volver a hacer el test
      </button>
    </div>;
}